import {
  ICartControllerApi,
  CartStoreProps,
  SettingsOverrides,
  VeloInputs,
  ExperimentProps,
} from '../../types/app.types';
import {CartService} from '../services/CartService';
import {ProductsService} from '../services/ProductsService';
import {BIService} from '../services/BIService';
import {FedopsInteractions} from '../../common/constants';
import {StyleSettingsService} from '../services/StyleSettingsService';
import {SPECS} from '../specs';
import {MinimumOrderAmountService} from '../services/MinimumOrderAmountService';
import {calcTopThreeViolations} from '../utils/violations.utils';
import {LineItemModel} from '../models/LineItem.model';
import {ControllerParams} from '@wix/yoshi-flow-editor';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';

export class CartStore {
  private readonly biService: BIService;
  private readonly cartService: CartService;
  private readonly productsService: ProductsService;
  private readonly controllerApi: ICartControllerApi;
  private readonly flowAPI: ControllerParams['flowAPI'];
  private readonly styleSettingsService: StyleSettingsService;
  private readonly minimumOrderAmountService: MinimumOrderAmountService;
  private screenReaderMessage: string;
  private settingsOverrides: SettingsOverrides;

  constructor(
    flowAPI: ControllerParams['flowAPI'],
    controllerApi: ICartControllerApi,
    private readonly viewerContext: ViewerContext,
    {
      biService,
      cartService,
      productsService,
      styleSettingsService,
      minimumOrderAmountService,
    }: {
      biService: BIService;
      cartService: CartService;
      productsService: ProductsService;
      styleSettingsService: StyleSettingsService;
      minimumOrderAmountService: MinimumOrderAmountService;
    },
    veloInputs: VeloInputs
  ) {
    this.setOverrides(veloInputs);
    this.controllerApi = controllerApi;
    this.flowAPI = flowAPI;
    this.biService = biService;
    this.cartService = cartService;
    this.productsService = productsService;
    this.styleSettingsService = styleSettingsService;
    this.minimumOrderAmountService = minimumOrderAmountService;
  }

  private readonly updateItemQuantity = async (cartItemId: string, quantity: number) => {
    this.cartService.showLoaderOnItem(cartItemId);
    await this.controllerApi.updateComponent();
    return this.controllerApi.reportFedops(FedopsInteractions.UpdateQuantity, () =>
      this.cartService.updateItemQuantity(cartItemId, quantity)
    );
  };

  private readonly removeItemFromCart = async (lineItem: LineItemModel) => {
    this.cartService.showLoaderOnItem(lineItem.id);
    await this.controllerApi.updateComponent();
    this.screenReaderMessage = this.controllerApi.t('cart.sr_item_was_removed', {item_name: lineItem.productName});
    return this.controllerApi.reportFedops(FedopsInteractions.RemoveItem, () =>
      this.cartService.removeItemFromCart(lineItem)
    );
  };

  private readonly applyCoupon = async (code: string) => {
    await this.controllerApi.reportFedops(FedopsInteractions.ApplyCoupon, () => this.cartService.applyCoupon(code));
  };

  private readonly removeCoupon = async () => {
    return this.controllerApi.reportFedops(FedopsInteractions.ApplyCoupon, () => this.cartService.removeCoupon());
  };

  private readonly sendToggleCouponBi = () => {
    return this.biService.clickOnApplyPromotionalCodeSf(this.cartService.cartModel);
  };

  private readonly sendAddNoteBi = () => {
    return this.biService.clickOnAddNoteToSellerSf(this.cartService.cartModel);
  };

  private readonly sendMinimumOrderMessageShownBi = (buttonEnabled: boolean) => {
    return this.biService.minimumOrderMessageIsShownInCart(
      this.cartService.cartModel,
      this.cartService.estimatedTotals,
      buttonEnabled
    );
  };

  private readonly sendEstimateTotalsErrorClickOnTryAgainBi = () => {
    return this.biService.cartEstimateTotalsErrorClickOnTryAgain(
      this.cartService.cartModel,
      this.cartService.cartLayout
    );
  };

  private readonly reportClickToOpenCloseMoreDetailsBi = ({
    lineItemId,
    action,
  }: {
    lineItemId: string;
    action: string;
  }) => {
    return this.biService.reportClickToOpenCloseMoreDetails({
      cartModel: this.cartService.cartModel,
      lineItemId,
      action,
    });
  };

  private readonly updateBuyerNote = async (...args: Parameters<CartService['updateBuyerNote']>) => {
    return this.cartService.updateBuyerNote(...args);
  };

  private readonly fetchEstimateTotals = async () => {
    return this.cartService.fetchEstimateTotals();
  };

  private readonly getProductsManifest = () => {
    return this.productsService.manifest(this.cartService.cartModel);
  };

  private get isCartValid(): boolean {
    return this.cartService.areAllItemsInStock;
  }

  private get isPartiallyPaid(): boolean {
    return this.cartService.estimatedTotals?.payLater.total.convertedAmount > 0;
  }

  private get shouldDisplayViolations(): boolean {
    return this.cartService.estimatedTotals?.violations?.length > 0;
  }

  private readonly lineItemsLoaded = () => {
    this.flowAPI.fedops.interactionEnded(FedopsInteractions.lineItemsLoaded);
  };

  private readonly pageLoaded = () => {
    this.flowAPI.fedops.interactionEnded(FedopsInteractions.padeLoaded);
  };

  private readonly reportCheckoutButtonPresented = (isButtonShown: boolean) => {
    this.cartService.reportCheckoutButtonPresented(isButtonShown);
  };

  private readonly reportCartButtonPresented = (isButtonShown: boolean) => {
    this.cartService.reportCartButtonPresented(isButtonShown);
  };

  private readonly reportPromoCodePresented = (isPromoCodeShown: boolean) => {
    this.cartService.reportPromoCodePresented(isPromoCodeShown);
  };

  private readonly reportBuyerNotePresented = (isBuyerNoteShown: boolean) => {
    this.cartService.reportBuyerNotePresented(isBuyerNoteShown);
  };

  public setOverrides(veloInputs: VeloInputs) {
    this.settingsOverrides = {
      ...this.settingsOverrides,
      ...veloInputs,
    };
  }

  public async toProps(): Promise<CartStoreProps> {
    const experimentProps: ExperimentProps = {
      shouldPresentTooltipWithoutNumber: this.viewerContext.isExperimentEnabled(SPECS.CartTooltipWithoutNumber),
      AddSlotToCartPage: this.viewerContext.isExperimentEnabled(SPECS.AddSlotToCartPage),
      sideCartElementsVisibilityInCss: this.viewerContext.isExperimentEnabled(SPECS.SideCartElementsVisibilityInCss),
      mapCartSummaryColors: this.viewerContext.isExperimentEnabled(SPECS.MapCartSummaryColors),
      isGiftCardMixedCartEnabled: this.viewerContext.isExperimentEnabled(SPECS.GiftCardAddToCartSettings),
      useProductThumbnailWithWowImage: this.viewerContext.isExperimentEnabled(SPECS.UseProductThumbnailWithWowImage),
      useDeliveryOptionsLibInCart: this.viewerContext.isExperimentEnabled(SPECS.UseDeliveryOptionsLibInCart),
      fixCouponCss: this.viewerContext.isExperimentEnabled(SPECS.FixCouponCss),
      emptyCartA11yFix: this.viewerContext.isExperimentEnabled(SPECS.EmptyCartA11yFix),
      sideCartCouponAndNote: this.viewerContext.isExperimentEnabled(SPECS.SideCartCouponAndNote),
      fixExpressWrongTotalAmount: this.viewerContext.isExperimentEnabled(SPECS.FixExpressWrongTotalAmount),
      cartButtonBIEvents: this.viewerContext.isExperimentEnabled(SPECS.CartButtonBIEvents),
      sideCartNote: this.viewerContext.isExperimentEnabled(SPECS.SideCartNote),
      deprecatePaypalOnlyFlowInCart: this.viewerContext.isExperimentEnabled(SPECS.DeprecatePaypalOnlyFlowInCart),
      expressCheckoutButtonsSkeleton: this.viewerContext.isExperimentEnabled(SPECS.ExpressCheckoutButtonsSkeleton),
      sendBiFromDeliveryDropdown: this.viewerContext.isExperimentEnabled(SPECS.SendBiFromDeliveryDropdown),
      removeBackgroudColorCartItemThumbnail: this.viewerContext.isExperimentEnabled(
        SPECS.RemoveBackgroudColorCartItemThumbnail
      ),
      fixExpressCheckoutButtonOnEditor: this.viewerContext.isExperimentEnabled(SPECS.FixExpressCheckoutButtonOnEditor),
      supportMembershipOnlineWithOfflineRemainderPaymentOption: this.viewerContext.isExperimentEnabled(
        SPECS.SupportMembershipOnlineWithOfflineRemainderPaymentOption
      ),
      freeDeliveryCouponTextOnCart: this.viewerContext.isExperimentEnabled(SPECS.FreeDeliveryCouponTextOnCart),
      fixTypingTextInNoteFieldIsBuggy: this.viewerContext.isExperimentEnabled(SPECS.FixTypingTextInNoteFieldIsBuggy),
      sendBIForPromoCodeAndNoteDisplay: this.viewerContext.isExperimentEnabled(SPECS.SendBIForPromoCodeAndNoteDisplay),
    };

    if (this.cartService.isEmpty) {
      return {
        shouldRenderEmptyState: true,
        lineItemsLoaded: this.lineItemsLoaded,
        pageLoaded: this.pageLoaded,
        ...experimentProps,
      } as unknown as CartStoreProps;
    }

    return {
      loadingItems: this.cartService.loadingItems,
      cartModel: this.cartService.cartModel,
      estimatedTotals: this.cartService.estimatedTotals,
      itemsCount: this.cartService.cartModel?.lineItems?.reduce((count, lineItem) => count + lineItem.quantity, 0),
      isNonShippableCart: this.cartService.isNonShippableCart,
      manifest: await this.getProductsManifest(),
      applyCoupon: this.applyCoupon,
      removeCoupon: this.removeCoupon,
      couponError: this.cartService.couponError,
      isCouponUpdateInProgress: this.cartService.isCouponUpdateInProgress,
      isFreeCart: this.cartService.isZeroCart,
      sendToggleCouponBi: this.sendToggleCouponBi,
      removeItemFromCart: this.removeItemFromCart,
      sendAddNoteBi: this.sendAddNoteBi,
      sendMinimumOrderMessageShownBi: this.sendMinimumOrderMessageShownBi,
      sendEstimateTotalsErrorClickOnTryAgainBi: this.sendEstimateTotalsErrorClickOnTryAgainBi,
      reportClickToOpenCloseMoreDetailsBi: this.reportClickToOpenCloseMoreDetailsBi,
      reportCartButtonPresented: this.reportCartButtonPresented,
      reportPromoCodePresented: this.reportPromoCodePresented,
      reportBuyerNotePresented: this.reportBuyerNotePresented,
      reportCheckoutButtonPresented: this.reportCheckoutButtonPresented,
      updateBuyerNote: this.updateBuyerNote,
      updateItemQuantity: this.updateItemQuantity,
      screenReaderMessage: this.screenReaderMessage,
      isCartValid: this.isCartValid,
      shouldShowCoupon: this.styleSettingsService.shouldShowCoupon,
      shouldShowBuyerNote: this.styleSettingsService.shouldShowBuyerNote,
      shouldShowMinimumOrderAmount: this.minimumOrderAmountService.shouldDisplayNotification,
      shouldShowTopCheckoutButtonInMobile: this.styleSettingsService.shouldShowTopCheckoutButtonInMobile,
      shouldDisplayViolations: this.shouldDisplayViolations,
      shouldShowSecureCheckout: this.settingsOverrides.shouldShowSecureCheckout,
      isPartiallyPaid: this.isPartiallyPaid,
      hasErrorViolations: this.cartService.hasErrorViolations,
      topThreeViolations: calcTopThreeViolations(this.cartService.estimatedTotals?.violations),
      ...experimentProps,
      shouldShowError: this.cartService.hasError,
      errorOnEstimateTotals: this.cartService.errorOnEstimateTotals,
      clearError: this.cartService.clearError,
      lineItemsLoaded: this.lineItemsLoaded,
      pageLoaded: this.pageLoaded,
      isSummaryUpdating: this.cartService.isSummaryUpdating,
      isSummaryLoading: this.cartService.isSummaryLoading,
      shouldEstimateTotals: this.cartService.shouldEstimateTotals(),
      fetchEstimateTotals: this.fetchEstimateTotals,
    };
  }
}
